import type { Translation } from "@/i18n/locales/types";

export const certificates: Translation["certificates"] = {
  title: "Certificates",
  new: "Add certificate",
  replace: "Replace certificate",
  detail: "Certificate",
  tabs: {
    overview: "Overview",
    versions: "Versions",
    versionsCount: "Versions ({{count}})",
    access: "Who can use it",
    activity: "Activity",
  },
  list: {
    description:
      "Your organisation's certificates: who uses them and when they expire.",
    search: "Search by name, holder or tax ID",
    filters: {
      type: "Type",
      status: "Status",
      expiring: "Expires",
      expiringIn: "In less than {{count}} days",
    },
    columns: {
      name: "Name",
      company: "Company · Tax ID",
      type: "Type",
      issuer: "Issuer",
      expires: "Expires",
      people: "People",
      status: "Status",
    },
    peopleHint: "People with effective access according to the policies",
    emptyTitle: "No certificates yet",
    emptyDescription:
      "Add the first one by uploading its .p12: the key goes straight to KMS and the file is not stored.",
  },
  expiry: {
    inDays_one: "in {{count}} day",
    inDays_other: "in {{count}} days",
    expired: "Expired",
    none: "No active version",
  },
  actions: {
    delete: "Delete",
    replace: "Replace",
    suspend: "Suspend",
    reactivate: "Reactivate",
    rename: "Rename",
  },
  suspend: {
    title: "Suspend this certificate?",
    description:
      "It stops signing immediately on every Mac and disappears from the app. Nothing is deleted: you can reactivate it whenever you want.",
    reason: "Reason (kept in the log)",
    reasonPlaceholder: "For example: reviewing September's invoices",
    confirm: "Suspend",
    done: "Certificate suspended",
  },
  delete: {
    title: "Delete this certificate?",
    description:
      "It has never been active: its pending versions are deleted and, if the key was imported, its destruction is scheduled.",
    confirm: "Delete",
    done: "Certificate deleted",
  },
  reactivate: {
    title: "Reactivate this certificate?",
    description:
      "It signs again as soon as the Macs receive it, according to the policies that include it.",
    confirm: "Reactivate",
    done: "Certificate reactivated",
  },
  suspendedBanner: "Suspended {{date}}: {{reason}}",
  noValidVersionBanner:
    "It has no valid version, so it does not sign. Upload a new one with “Replace”.",
  expiringBanner:
    "The active version expires {{when}}. Replace it before then so nobody loses access.",
  overview: {
    certificate: "Certificate",
    key: "Key",
    dates: "Dates",
    name: "Name",
    description: "Description",
    holder: "Holder",
    holderTaxId: "Holder tax ID",
    company: "Company",
    companyTaxId: "Company tax ID",
    issuer: "Issuer",
    type: "Type",
    serial: "Serial number",
    sha1: "SHA-1 fingerprint",
    sha256: "SHA-256 fingerprint",
    subject: "Subject",
    issuerDn: "Issuer (DN)",
    keyUsages: "Key usages",
    extendedKeyUsages: "Extended key usages",
    keyBackend: "Where the key is",
    keyBackends: {
      kms: "AWS KMS (non-exportable)",
      local: "Local file (development only)",
    },
    keySpec: "Key type",
    keyRef: "Reference",
    validity: "Validity",
    validityRange: "{{from}} – {{to}}",
    activatedAt: "Active since",
    createdAt: "Added",
    noActiveVersion: "There is no active version.",
    renameTitle: "Name and description",
    renameHint:
      "You choose the name; company, tax ID, type and issuer come from the certificate.",
    nameRequired: "Enter a name",
    saved: "Name saved",
  },
  versions: {
    description:
      "Policies and people point to the certificate, not to a version: they stay the same when you replace it.",
    columns: {
      version: "Version",
      holder: "Holder",
      validity: "Validity",
      status: "Status",
      actions: "Actions",
    },
    number: "v{{number}}",
    active: "Active",
    retiredReplaced: "Retired (replaced)",
    retiredCompromised: "Retired (compromised)",
    keyDestroyedIn_one: "key destroyed in {{count}} day",
    keyDestroyedIn_other: "key destroyed in {{count}} days",
    keyDestroyed: "key destroyed",
    scheduledFor: "Activates on {{date}}",
    rollback: "Roll back to this",
    rollbackTitle: "Roll back to version {{number}}?",
    rollbackDescription:
      "Its key is re-enabled, its destruction is cancelled and it becomes the active one. The current one is retired and you can also roll back to it for 30 days.",
    rollbackDone: "Rolled back to version {{number}}",
    activate: "Activate now",
    activateTitle: "Activate version {{number}}?",
    activateDescription:
      "It becomes active immediately; the previous one is retired, its key disabled and destroyed in 30 days (you can roll back meanwhile).",
    activated: "Version {{number}} is active",
    unschedule: "Cancel schedule",
    unscheduled: "Schedule cancelled",
    retire: "Retire",
    retireTitle: "Retire version {{number}}?",
    retireDescription:
      "Its key is disabled immediately. If it is the active one and there is no other, the certificate stops signing.",
    retireReason: "Reason",
    retireReplaced:
      "No longer used: the key is destroyed in 30 days and you can roll back to it",
    retireCompromised:
      "Compromised or revoked: the key is destroyed in 7 days, with no way back",
    retireNote: "Note (optional)",
    retired: "Version retired",
    download: "Download certificate",
    downloaded: "Certificate downloaded",
    empty: "This certificate has no versions yet.",
  },
  access: {
    count_one: "{{count}} person can use it",
    count_other: "{{count}} people can use it",
    none: "Nobody can use it yet",
    noneDescription:
      "Without a policy that allows it, it is neither visible nor usable (denied by default).",
    giveAccess: "Give access",
    giveAccessNew: "In a new policy",
    giveAccessExisting: "Add it to an existing policy",
    columns: {
      person: "Person",
      where: "Where",
      policy: "Through the policy",
    },
    except: "except {{what}}",
    viaGroup: "group {{name}}",
    policies: "Policies that include it",
    noPolicies: "No policy includes it.",
    disabled: "disabled",
  },
  activity: {
    signatures: "Latest signatures",
    navigation: "Latest browsing",
    seeAll: "See everything in Activity",
    emptySignatures: "Nothing has been signed with this certificate yet.",
    emptyNavigation: "No browsing has been logged with this certificate.",
    columns: {
      when: "When",
      person: "Person",
      where: "Where",
      result: "Result",
      request: "Request",
    },
  },
  wizard: {
    steps: {
      upload: "Upload the .p12",
      review: "Review",
      activate: "Activate",
      name: "Name and access",
    },
    stepOf: "Step {{current}} of {{total}}",
    upload: {
      file: ".p12 or .pfx file",
      fileHint: "64 KB at most.",
      fileRequired: "Choose the certificate file",
      fileType: "It has to be a .p12 or .pfx",
      fileTooBig: "The file can't be larger than 64 KB",
      password: "File password",
      passwordRequired: "Enter the password",
      notStored:
        "Neither the file nor the password are stored: the private key is imported straight into AWS KMS as non-exportable and the .p12 is never written to disk.",
      kmsLink: "I already have the key in KMS",
      p12Link: "Upload a .p12",
      keyRef: "KMS key ARN or ID",
      keyRefRequired: "Enter the key ARN",
      certificate: "Certificate (.cer, .crt, .pem or .der)",
      certificateRequired: "Choose the certificate",
      kmsHint:
        "For rare cases: the key is already in KMS (or CloudHSM). The certificate's public key is checked against the one in KMS.",
      submit: "Continue",
      uploading: "Checking the certificate…",
      duplicate: "This certificate is already registered in the organization.",
      wrongPassword: "The password is not correct",
    },
    review: {
      title: "Review the certificate",
      newTitle: "This is what the certificate contains",
      current: "Active version",
      next: "New",
      changes: "changes",
      company: "Company",
      companyTaxId: "Tax ID",
      holder: "Holder",
      issuer: "Issuer",
      type: "Type",
      validity: "Validity",
      until: "until {{date}}",
      serial: "Serial number",
      keep: "Policies and people stay the same: they point to the certificate, not to this version.",
      holderChanged:
        "The holder changes. Policies and people stay the same: they point to the certificate, not to this version.",
      issuerChanged: "The certificate issuer changes.",
      noActive: "There is no active version to compare with.",
      taxIdTitle: "The company tax ID changes",
      taxIdDescription:
        "The new certificate belongs to {{newCompany}} ({{newTaxId}}) and the current one to {{previousCompany}} ({{previousTaxId}}). If this is right, type the new tax ID to continue: everyone with access will sign on behalf of a different company.",
      taxIdInput: "New tax ID",
      taxIdMismatch: "It doesn't match the new certificate's tax ID",
      taxIdConfirm: "Confirm the tax ID change",
    },
    activate: {
      title: "When does it become active?",
      now: "Activate now",
      nowHint:
        "The current version is retired immediately. You can roll back to it for 30 days.",
      schedule: "Schedule",
      scheduleHint: "Until then the current version keeps signing.",
      at: "Date and time",
      atRequired: "Pick a future date and time",
      submitNow: "Activate",
      submitSchedule: "Schedule",
      doneNow: "Certificate replaced: version {{number}} is now active",
      doneScheduled: "Replacement scheduled for {{date}}",
    },
    name: {
      title: "Give it a name",
      name: "Name",
      nameHint:
        "This is what people will see in the app and in “My certificates”.",
      nameRequired: "Enter a name",
      description: "Description (optional)",
      giveAccess: "Give access now",
      giveAccessHint:
        "Opens the policy editor with this certificate already added. Otherwise nobody can use it until you add it to a policy.",
      submit: "Add certificate",
      done: "Certificate added",
    },
    cancel: "Cancel",
  },
};
